import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const AuditLogs = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, successful, failed, rate_limit

  useEffect(() => {
    fetchLogs();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchLogs, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLogs = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await makeAuthenticatedRequest(`${apiUrl}/audit-logs`);

      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getFilteredLogs = () => {
    if (filter === 'all') return logs;
    if (filter === 'successful') return logs.filter(log => log.type === 'successful_login');
    if (filter === 'failed') return logs.filter(log => log.type === 'failed_login');
    if (filter === 'rate_limit') return logs.filter(log => log.type === 'rate_limit_exceeded');
    return logs;
  };

  const getLogIcon = (type) => {
    switch (type) {
      case 'successful_login':
        return '✅';
      case 'failed_login':
        return '⚠️';
      case 'rate_limit_exceeded':
        return '🚫';
      default:
        return '📝';
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case 'successful_login':
        return 'bg-green-50 border-green-200';
      case 'failed_login':
        return 'bg-yellow-50 border-yellow-200';
      case 'rate_limit_exceeded':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getLogTitle = (type) => {
    switch (type) {
      case 'successful_login':
        return 'Successful Login';
      case 'failed_login':
        return 'Failed Login';
      case 'rate_limit_exceeded':
        return 'Rate Limit Exceeded';
      default:
        return 'Unknown Event';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const filteredLogs = getFilteredLogs();

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="text-3xl sm:text-4xl mb-4">📊</div>
        <p className="text-sm sm:text-base text-gray-600">Loading audit logs...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b pb-3 sm:pb-4 gap-3">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Audit Logs</h2>
          <p className="text-xs sm:text-sm text-gray-500">Recent authentication events (last 100)</p>
        </div>
        <button
          onClick={fetchLogs}
          className="bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm w-full sm:w-auto"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All ({logs.length})
        </button>
        <button
          onClick={() => setFilter('successful')}
          className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
            filter === 'successful'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          ✅ Successful ({logs.filter(l => l.type === 'successful_login').length})
        </button>
        <button
          onClick={() => setFilter('failed')}
          className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
            filter === 'failed'
              ? 'bg-yellow-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          ⚠️ Failed ({logs.filter(l => l.type === 'failed_login').length})
        </button>
        <button
          onClick={() => setFilter('rate_limit')}
          className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
            filter === 'rate_limit'
              ? 'bg-red-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          🚫 Rate Limited ({logs.filter(l => l.type === 'rate_limit_exceeded').length})
        </button>
      </div>

      {/* Logs List */}
      {filteredLogs.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-4xl sm:text-5xl mb-4">📭</div>
          <p className="text-sm sm:text-base text-gray-600">No audit logs yet</p>
          <p className="text-xs sm:text-sm text-gray-500 mt-2">Login events will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredLogs.map((log, index) => (
            <div
              key={index}
              className={`border rounded-lg p-3 sm:p-4 ${getLogColor(log.type)}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 sm:gap-3 flex-1">
                  <span className="text-xl sm:text-2xl">{getLogIcon(log.type)}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-800">
                      {getLogTitle(log.type)}
                    </h3>
                    <div className="mt-2 space-y-1 text-xs sm:text-sm">
                      {log.username && (
                        <p className="text-gray-700">
                          <span className="font-medium">Username:</span> {log.username}
                        </p>
                      )}
                      <p className="text-gray-700">
                        <span className="font-medium">IP:</span> {log.ip}
                      </p>
                      {log.reason && (
                        <p className="text-gray-700">
                          <span className="font-medium">Reason:</span>{' '}
                          {log.reason === 'username_not_found' ? 'Username not found' : 'Invalid password'}
                        </p>
                      )}
                      {log.retryAfter && (
                        <p className="text-gray-700">
                          <span className="font-medium">Retry After:</span> {log.retryAfter} seconds
                        </p>
                      )}
                      {log.userAgent && (
                        <p className="text-gray-700 break-all">
                          <span className="font-medium">User Agent:</span>{' '}
                          <span className="text-xs">{log.userAgent}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs text-gray-500 whitespace-nowrap">
                    {formatTimestamp(log.timestamp)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200">
        <h3 className="font-semibold text-blue-800 mb-2 text-sm sm:text-base">💡 About Audit Logs</h3>
        <ul className="text-xs sm:text-sm text-blue-700 space-y-1">
          <li>• Shows the last 100 authentication events</li>
          <li>• Auto-refreshes every 10 seconds</li>
          <li>• Logs are stored in memory (resets on worker restart)</li>
          <li>• Track successful logins, failed attempts, and rate limiting</li>
        </ul>
      </div>
    </div>
  );
};

export default AuditLogs;
